// /composables/useWheelAnalysisQuery.js
import { useQuery } from '@tanstack/vue-query'
import axios from 'axios'

import { useAnalysisStore } from '@/stores/Setup/analysis'
import { useAllGoalsStore } from '@/stores/WheelAnalysis/allGoals'
import { useAdditionalGoalsStore } from '@/stores/WheelAnalysis/additionalGoals'
import { useCriticalGoalsStore } from '@/stores/WheelAnalysis/criticalGoals'
import { useGoalAStore } from '@/stores/WheelAnalysis/goalA'
import { useGoalBStore } from '@/stores/WheelAnalysis/goalB'
import { useChallengeStore } from '@/stores/WheelAnalysis/challenge'
import { useKeywordStore } from '@/stores/WheelAnalysis/keyword'

// ---------- helpers ---------------------------------------------------------

// Parse values that may be JSON, double-JSON, or already arrays/objects
function parseJSONSafely(v) {
    if (v == null) return null
    if (Array.isArray(v) || typeof v === 'object') return v

    try {
        const once = JSON.parse(v)
        if (Array.isArray(once) || typeof once === 'object') return once

        if (typeof once === 'string') {
            try {
                const twice = JSON.parse(once)
                if (Array.isArray(twice) || typeof twice === 'object') return twice
            } catch (e2) {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.debug('parseJSONSafely: not double-encoded JSON', e2)
                }
            }
        }
    } catch (e1) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('parseJSONSafely: not JSON', e1)
        }
        return null
    }
    return null
}

const looksNonEmptyArray = (a) => Array.isArray(a) && a.length > 0
const looksNonEmptyMatrix = (m) => Array.isArray(m) && m.length > 0 && Array.isArray(m[0])

// Does the array contain at least one `true`? (accepts 'true' as well)
function arrayHasAnyTrue(a) {
    return Array.isArray(a) && a.some((v) => v === true || v === 'true')
}

// Does the matrix contain at least one `true`? (accepts 'true' as well)
function matrixHasAnyTrue(m) {
    if (!Array.isArray(m)) return false
    for (let i = 0; i < m.length; i += 1) {
        const row = m[i]
        if (Array.isArray(row) && row.some((v) => v === true || v === 'true')) return true
    }
    return false
}

// Assign once on first hydration, or when server payload brings any `true`
function guardedAssignArray(storeObj, storeKeyFlag, payload, assign) {
    const parsed = parseJSONSafely(payload)
    if (!looksNonEmptyArray(parsed)) return false

    const hasTrue = arrayHasAnyTrue(parsed)
    if (storeObj[storeKeyFlag] === true && !hasTrue) return false

    assign(parsed)
    storeObj[storeKeyFlag] = true
    return true
}

function guardedAssignMatrix(storeObj, storeKeyFlag, payload, assign) {
    const parsed = parseJSONSafely(payload)
    if (!looksNonEmptyMatrix(parsed)) return false

    const hasTrue = matrixHasAnyTrue(parsed)
    if (storeObj[storeKeyFlag] === true && !hasTrue) return false

    assign(parsed)
    storeObj[storeKeyFlag] = true
    return true
}

// Normalizes array of booleans/strings to real booleans
function normalizeBoolArray(arr, lengthHint = null) {
    const src = Array.isArray(arr) ? arr : []
    const out = src.map((v) => v === true || v === 'true')
    if (lengthHint != null && out.length < lengthHint) {
        return out.concat(Array(lengthHint - out.length).fill(false))
    }
    return out
}

// Safe integer-or-null
function toIntOrNull(v) {
    if (v === null || v === undefined) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

// ---------------------------------------------------------------------------

export default () => {
    const analysisStore = useAnalysisStore()
    const allGoalsStore = useAllGoalsStore()
    const additionalGoalsStore = useAdditionalGoalsStore()
    const criticalGoalsStore = useCriticalGoalsStore()
    const goalAStore = useGoalAStore()
    const goalBStore = useGoalBStore()
    const challengeStore = useChallengeStore()
    const keywordStore = useKeywordStore()

    return useQuery({
        queryKey: ['wheelAnalysis', analysisStore.wheelAnalysisHexCode],
        enabled: !!analysisStore.wheelAnalysisHexCode,
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 1000,

        queryFn: async () => {
            const url = `${process.env.VUE_APP_API_ROOT}api/v1/wheel_analyses/${analysisStore.wheelAnalysisHexCode}`

            // guard watchers during hydration
            allGoalsStore.isHydrating = true
            // make sure critical goals won’t auto-save during hydration either
            if ('isHydrating' in criticalGoalsStore) criticalGoalsStore.isHydrating = true

            try {
                const response = await axios.get(url)
                const data = response?.data ?? {}

                // ----- numeric id for PUTs -----
                if (data.id != null) {
                    analysisStore.wheelAnalysisId = data.id
                }

                // ----- challenge meta -----
                challengeStore.challengeType = data.challenge_type
                challengeStore.challengeTypeOther = data.challenge_type_other
                challengeStore.challengeStatement = data.challenge_statement

                // =======================
                // Critical Goals (A/B) — indices + selections
                // =======================

                // Goal A / Goal B indices (null if not present)
                criticalGoalsStore.goalAIndex = toIntOrNull(data.goal_a_index)
                criticalGoalsStore.goalBIndex = toIntOrNull(data.goal_b_index)

                // selected_critical_goals from API takes precedence if present
                let appliedCriticalFromApi = false
                if (data.selected_critical_goals != null) {
                    const parsed = parseJSONSafely(data.selected_critical_goals)
                    if (looksNonEmptyArray(parsed)) {
                        const normalized = normalizeBoolArray(parsed, 17)
                        // use the store’s setter to keep logic consistent; hydration guard prevents saves
                        if (typeof criticalGoalsStore.setSelectedCriticalGoals === 'function') {
                            criticalGoalsStore.setSelectedCriticalGoals(normalized)
                        } else {
                            criticalGoalsStore.selectedCriticalGoals = normalized
                        }
                        appliedCriticalFromApi = true
                    }
                }

                // =======================
                // Goal A / Goal B targets (assign if present & shaped)
                // =======================
                if (data.goal_a_selected_targets != null) {
                    const a = parseJSONSafely(data.goal_a_selected_targets)
                    if (looksNonEmptyArray(a) || looksNonEmptyMatrix(a)) {
                        goalAStore.selectedTargets = a
                    }
                }
                if (data.goal_b_selected_targets != null) {
                    const b = parseJSONSafely(data.goal_b_selected_targets)
                    if (looksNonEmptyArray(b) || looksNonEmptyMatrix(b)) {
                        goalBStore.selectedTargets = b
                    }
                }

                // =======================
                // All Goals (prefer all_goals_*, fallback to legacy names)
                // =======================

                // selectedGoals: guard overwrite unless first hydration or payload has any true
                const assignedAllGoals =
                    guardedAssignArray(
                        allGoalsStore,
                        '_hydratedAllGoalsSelectedGoals',
                        data.all_goals_selected_goals,
                        (arr) => { allGoalsStore.selectedGoals = arr.map(v => v === true || v === 'true') }
                    ) ||
                    guardedAssignArray(
                        allGoalsStore,
                        '_hydratedAllGoalsSelectedGoals',
                        data.selected_goals, // legacy
                        (arr) => { allGoalsStore.selectedGoals = arr.map(v => v === true || v === 'true') }
                    )

                // If the API did not give us explicit selected_critical_goals,
                // fall back to mirroring AllGoals -> CriticalGoals (legacy behavior)
                if (!appliedCriticalFromApi && assignedAllGoals) {
                    const mirrored = normalizeBoolArray(allGoalsStore.selectedGoals, 17)
                    if (typeof criticalGoalsStore.setSelectedCriticalGoals === 'function') {
                        criticalGoalsStore.setSelectedCriticalGoals(mirrored)
                    } else {
                        criticalGoalsStore.selectedCriticalGoals = mirrored
                    }
                }

                // selectedTargets: guard overwrite unless first hydration or payload has any true
                guardedAssignMatrix(
                    allGoalsStore,
                    '_hydratedAllGoalsSelectedTargets',
                    data.all_goals_selected_targets,
                    (grid) => { allGoalsStore.selectedTargets = grid }
                ) || guardedAssignMatrix(
                    allGoalsStore,
                    '_hydratedAllGoalsSelectedTargets',
                    data.selected_targets, // legacy
                    (grid) => { allGoalsStore.selectedTargets = grid }
                )

                // ----- AdditionalGoals (mirror sink) -----
                const addGoalsParsed = parseJSONSafely(data.additional_goals_selected_goals)
                if (looksNonEmptyArray(addGoalsParsed)) {
                    additionalGoalsStore.selectedGoals = addGoalsParsed.map(v => v === true || v === 'true')
                }
                const addTargetsParsed = parseJSONSafely(data.additional_goals_selected_targets)
                if (looksNonEmptyMatrix(addTargetsParsed)) {
                    additionalGoalsStore.selectedTargets = addTargetsParsed
                }

                // ----- Notes & keywords -----
                allGoalsStore.reviewNotes = data.notes ?? null
                keywordStore.keyword1 = data.keyword1
                keywordStore.keyword2 = data.keyword2
                keywordStore.keyword3 = data.keyword3

                return response
            } finally {
                allGoalsStore.isHydrating = false
                if ('isHydrating' in criticalGoalsStore) criticalGoalsStore.isHydrating = false
            }
        },
    })
}
